import { Loader2 } from "lucide-react";

const STATUS_STYLES = {
  ready: { bg: "rgba(16,185,129,0.15)", c: "#10B981", brd: "#10B981" },
  paid: { bg: "rgba(16,185,129,0.15)", c: "#10B981", brd: "#10B981" },
  certified: { bg: "rgba(16,185,129,0.15)", c: "#10B981", brd: "#10B981" },
  in_use: { bg: "rgba(6,182,212,0.15)", c: "#06B6D4", brd: "#06B6D4" },
  in_progress: { bg: "rgba(6,182,212,0.15)", c: "#06B6D4", brd: "#06B6D4" },
  deposit_paid: { bg: "rgba(6,182,212,0.15)", c: "#06B6D4", brd: "#06B6D4" },
  warning: { bg: "rgba(245,158,11,0.15)", c: "#F59E0B", brd: "#F59E0B" },
  maintenance_required: { bg: "rgba(245,158,11,0.15)", c: "#F59E0B", brd: "#F59E0B" },
  restock_required: { bg: "rgba(245,158,11,0.15)", c: "#F59E0B", brd: "#F59E0B" },
  payment_pending: { bg: "rgba(245,158,11,0.15)", c: "#F59E0B", brd: "#F59E0B" },
  pending: { bg: "rgba(245,158,11,0.15)", c: "#F59E0B", brd: "#F59E0B" },
  missing: { bg: "rgba(239,68,68,0.15)", c: "#EF4444", brd: "#EF4444" },
  damaged: { bg: "rgba(239,68,68,0.15)", c: "#EF4444", brd: "#EF4444" },
  declined: { bg: "rgba(239,68,68,0.15)", c: "#EF4444", brd: "#EF4444" },
  locked: { bg: "rgba(239,68,68,0.15)", c: "#EF4444", brd: "#EF4444" },
  unpaid: { bg: "rgba(100,116,139,0.15)", c: "#94A3B8", brd: "#475569" },
  info: { bg: "rgba(59,130,246,0.15)", c: "#3B82F6", brd: "#3B82F6" },
  default: { bg: "rgba(100,116,139,0.15)", c: "#94A3B8", brd: "#475569" },
};

export function StatusChip({ status, label, testid }) {
  const s = STATUS_STYLES[status] || STATUS_STYLES.default;
  return (
    <span className="chip" data-testid={testid} style={{ background: s.bg, color: s.c, borderColor: s.brd }}>
      {label || (status || "").replace(/_/g, " ")}
    </span>
  );
}

export function StatCard({ label, value, sub, accent, testid, icon: Icon }) {
  return (
    <div className="sg-card p-4 sm:p-5 sg-fade" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider" style={{ color: "var(--txt2)" }}>{label}</span>
        {Icon && <Icon size={18} style={{ color: accent ? "var(--accent)" : "var(--txt2)" }} />}
      </div>
      <div className="font-head text-3xl sm:text-4xl font-extrabold mt-2" style={{ color: accent ? "var(--accent)" : "var(--txt)" }}>
        {value}
      </div>
      {sub && <div className="text-sm mt-1" style={{ color: "var(--txt2)" }}>{sub}</div>}
    </div>
  );
}

export function ProgressRing({ value, target, size = 190 }) {
  const pct = target > 0 ? Math.min(value / target, 1) : 0;
  const stroke = 16;
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c - pct * c;
  const reached = value >= target;
  return (
    <div style={{ position: "relative", width: size, height: size }} data-testid="daily-target-progress">
      <svg width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#1E293B" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke={reached ? "#10B981" : "var(--accent)"} strokeWidth={stroke} fill="none"
          strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
          style={{ transition: "stroke-dashoffset .5s ease" }}
        />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <div className="font-head text-3xl font-extrabold">${value.toLocaleString()}</div>
        <div className="text-sm" style={{ color: "var(--txt2)" }}>of ${target.toLocaleString()}</div>
        <div className="text-xs mt-1 font-mono" style={{ color: reached ? "#10B981" : "var(--accent)" }}>
          {Math.round(pct * 100)}%
        </div>
      </div>
    </div>
  );
}

export function SectionTitle({ children, sub, right }) {
  return (
    <div className="flex items-end justify-between mb-4">
      <div>
        <h2 className="font-head text-2xl sm:text-3xl font-bold">{children}</h2>
        {sub && <p className="text-sm mt-1" style={{ color: "var(--txt2)" }}>{sub}</p>}
      </div>
      {right}
    </div>
  );
}

export function FieldRule() {
  const lines = [
    "IF YOU DON'T KNOW — DON'T GUESS.",
    "IF YOU DON'T RECOGNIZE IT — DON'T SPRAY IT.",
    "IF IT ISN'T AUTHORIZED — DON'T TOUCH IT.",
    "IF IT ISN'T DOCUMENTED — IT DIDN'T HAPPEN.",
  ];
  return (
    <div className="sg-card p-4 sm:p-5" data-testid="freshsurf-field-rule"
      style={{ borderColor: "var(--accent)", background: "rgba(255,87,34,0.06)" }}>
      <div className="font-head text-sm font-bold uppercase tracking-widest mb-2" style={{ color: "var(--accent)" }}>
        FreshSurf Field Rule
      </div>
      {lines.map((l) => (
        <div key={l} className="font-head text-base sm:text-lg font-semibold leading-snug">{l}</div>
      ))}
    </div>
  );
}

export function Loading({ label = "Loading" }) {
  return (
    <div className="flex items-center gap-3 p-8" style={{ color: "var(--txt2)" }}>
      <Loader2 className="animate-spin" size={20} /> {label}…
    </div>
  );
}

export function Placeholder({ label = "TRAINER VALIDATION REQUIRED" }) {
  return (
    <span className="chip" style={{ background: "rgba(245,158,11,0.12)", color: "#F59E0B", borderColor: "#F59E0B" }}>
      {label}
    </span>
  );
}
